import { useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  Button,
  Spinner,
  Alert,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi'
import { useDailyReview } from '../../../hooks/useCampaign'
import { CanvasserPieChart } from '../dailyReview/CanvasserPieChart'
import { CanvasserStatsTable } from '../dailyReview/CanvasserStatsTable'
import { TodayNumbers } from './TodayNumbers'
import { TurfCoverageList } from '../dailyReview/TurfCoverageList'

interface Props {
  campaignId: string
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number)
  const d = new Date(year, month - 1, day)
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function TodayPanel({ campaignId }: Props) {
  const [reviewDate, setReviewDate] = useState(todayStr())
  const { data: review, isLoading, error, refetch } = useDailyReview(campaignId, reviewDate)

  const isToday = reviewDate === todayStr()

  const shiftDate = (days: number) => {
    const [y, m, d] = reviewDate.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    date.setDate(date.getDate() + days)
    setReviewDate(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`)
  }

  if (isLoading) {
    return (
      <VStack py={12}>
        <Spinner size="lg" color="accent" />
        <Text color="gray.500">Loading daily data...</Text>
      </VStack>
    )
  }

  if (!!error || !review) {
    return (
      <Alert.Root status="error" borderRadius="md">
        <Alert.Indicator />Failed to load daily review data.
              </Alert.Root>
    );
  }

  return (
    <VStack gap={5} align="stretch">
      {/* Date header with state indicator */}
      <HStack justify="space-between">
        <HStack gap={2}>
          <IconButton
            aria-label="Previous day"
            size="sm"
            variant="ghost"
            onClick={() => shiftDate(-1)}><FiChevronLeft /></IconButton>
          <Text fontWeight="medium" fontSize="md">{formatDate(reviewDate)}</Text>
          <IconButton
            aria-label="Next day"
            size="sm"
            variant="ghost"
            onClick={() => shiftDate(1)}><FiChevronRight /></IconButton>
          {!isToday && (
            <Button size="xs" variant="ghost" onClick={() => setReviewDate(todayStr())}>
              Today
            </Button>
          )}
        </HStack>
        <HStack gap={2}>
          {isToday ? (
            <Badge colorPalette="green" variant="subtle" px={2} py={0.5}>
              In Progress
            </Badge>
          ) : (
            <Badge colorPalette="gray" variant="outline" px={2} py={0.5}>
              Historical
            </Badge>
          )}
          <IconButton aria-label="Refresh" size="sm" variant="ghost" onClick={() => refetch()}><FiRefreshCw /></IconButton>
        </HStack>
      </HStack>

      {/* Two-column: Canvasser Performance (left) + Today's Numbers & Coverage (right) */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
        {/* Left column: Canvasser Performance */}
        {review.canvasserStats.length > 0 && (
          <Box bg="surface" p={5} borderRadius="lg" border="1px" borderColor="gray.200">
            <Text fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" mb={3}>
              Canvasser Performance
            </Text>
            <VStack gap={4} align="stretch">
              <CanvasserPieChart canvasserStats={review.canvasserStats} />
              <CanvasserStatsTable canvasserStats={review.canvasserStats} />
            </VStack>
          </Box>
        )}

        {/* Right column: Today's Numbers + Turf Coverage */}
        <VStack gap={4} align="stretch">
          <TodayNumbers
            doorsKnocked={review.doorsKnockedToday}
            contactsReached={review.contactsReachedToday}
            businessesVisited={review.canvasserStats.reduce((sum, c) => sum + c.businessesVisited, 0)}
            pendingCallbacks={review.pendingCallbacks}
          />

          {review.turfCoverage.length > 0 && (
            <Box bg="surface" p={5} borderRadius="lg" border="1px" borderColor="gray.200">
              <Text fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" mb={3}>
                Turf Coverage Detail
              </Text>
              <TurfCoverageList turfCoverage={review.turfCoverage} canvasserStats={review.canvasserStats} />
            </Box>
          )}
        </VStack>
      </SimpleGrid>
    </VStack>
  );
}
